import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import EventDetail from '@/components/results/eventDetail/eventDetail.vue';
import ConfirmDialog from '@/components/results/confirmDialog/confirmDialog.vue';
import { Confirmed } from '@/components/results/confirmDialog/confirmDialog';
import BetsDialog from '@/components/results/betsDialog/betsDialog.vue';
import QueuePanel from '@/components/results/queuePanel/queuePanel.vue';
import ActionLog from '@/components/results/actionLog/actionLog.vue';
import CommissionPanel from '@/components/results/commissionPanel/commissionPanel.vue';
import { AppConfig } from '@/config/app.config';
import { authService } from '@/services/auth.service';
import {
  Bet, declare, Declaration, Declared, FORCEABLE, idempotencyKey, loadBets, loadDebitCases, loadEvents, loadMarkets, loadRuns, Outcome,
  Product, ResultError, ResultEvent, ResultMarket, runStatus, View,
} from '@/results/results.api';
import { fancyView, isoDay, outcomesText, whenText } from '@/results/format';

/** GS-903: the console's sections; GS-904 commission rules. */
type Section = 'results' | 'approvals' | 'runs' | 'debits' | 'commission' | 'activity';

interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info';
  text: string;
}

/** What the operator chose in the detail panel, waiting for the confirmation. */
interface Choice {
  market: ResultMarket;
  action: Declaration['action'];
  winnerId?: number;
  winnerName?: string;
  value?: number;
  /** GS-894: a premium market's outcomes, and how the statement will read them. */
  results?: Outcome[];
  resultText?: string;
}

const SPORTS = [{ key: '1', label: 'Cricket' }, { key: '2', label: 'Soccer' }, { key: '3', label: 'Tennis' }];
const PRODUCTS = [{ key: '1', label: 'Match Odds' }, { key: '2', label: 'Bookmaker' }, { key: '3', label: 'Fancy' }, { key: '4', label: 'Premium' }];
/**
 * GS-927 — the products each sport has (owner, 2026-10-17: soccer and tennis have no bookmaker and no fancy). A product a
 * sport lacks is not offered, so no list can show a market under the wrong sport: the classic engine files a bet under the
 * sport its REQUEST named (bets.sport_id), and old test bets named the wrong one.
 */
const PRODUCTS_OF_SPORT: Record<number, number[]> = { 1: [1, 2, 3, 4], 2: [1, 4], 3: [1, 4] };
const PREFS_KEY = 'rb-result-ui.console';
const REFRESH_MS = 30000;
const POLL_MS = 1500;
const POLL_TRIES = 40;

/**
 * GS-901 — the result console: one place to result a match, whichever engine runs it. Pick the sport and the product;
 * the pending (or answered) matches of BOTH engines are listed; a match opens its markets beside the list (a sheet on a
 * phone or tablet); a result is confirmed in words and then followed until it is settled. Legacy matches go through
 * the old page's calls, engine v2's through SES's director API (GS-834) — a second supervisor approves corrections.
 */
@Component({ components: { EventDetail, ConfirmDialog, BetsDialog, QueuePanel, CommissionPanel, ActionLog } })
export default class ResultsConsole extends Vue {
  /** GS-903: the console's section — the results, or one of the operator queues. */
  private section: Section = 'results';
  private counts = { approvals: 0, blocked: 0, debits: 0 };
  private sportId = 1;
  private product: Product = 1;
  private view: View = 'pending';
  private from = isoDay(-2);
  private to = isoDay(0);
  private search = '';

  private events: ResultEvent[] = [];
  private errors: string[] = [];
  private loading = false;
  private selected: ResultEvent | null = null;
  private markets: ResultMarket[] = [];
  private marketsLoading = false;
  private marketsError = '';

  private choice: Choice | null = null;
  private dialog = {
    open: false, title: '', eyebrow: '', lines: [] as string[], confirmLabel: 'Confirm', tone: 'primary', needsReason: false,
    voidReasons: false, reopenOption: false, forceOption: false, outcomeOption: false, outcomeDefault: null as string | null,
    error: '', busy: false,
  };
  private bets = { open: false, market: null as ResultMarket | null, marketName: '', list: [] as Bet[], loading: false, error: '', busy: false };
  private toasts: Toast[] = [];
  private toastSeq = 0;
  private timer: number | undefined;

  private readonly sportTabs = SPORTS;
  get productTabs(): Array<{ key: string; label: string }> {
    const offered = PRODUCTS_OF_SPORT[this.sportId] || [1];
    return PRODUCTS.filter((p) => offered.includes(Number(p.key)));
  }

  /** The product kept when the sport changes: the same one if the sport has it, else Match Odds. */
  private productFor(sportId: number, product: number): Product {
    return ((PRODUCTS_OF_SPORT[sportId] || [1]).includes(product) ? product : 1) as Product;
  }

  get sectionTabs(): Array<{ key: string; label: string }> {
    const n = (count: number, word = '') => (count ? ` (${count}${word})` : '');
    return [
      { key: 'results', label: 'Results' },
      { key: 'approvals', label: 'Approvals' + n(this.counts.approvals) },
      { key: 'runs', label: 'Runs' + n(this.counts.blocked, ' blocked') },
      { key: 'debits', label: 'Debit cases' + n(this.counts.debits) },
      { key: 'commission', label: 'Commission' },
      // GS-925: who did what, for RCA
      { key: 'activity', label: 'Activity' },
    ];
  }

  get viewTabs(): Array<{ key: string; label: string }> {
    const pending = this.view === 'pending' ? ' (' + this.events.length + ')' : '';
    return [{ key: 'pending', label: 'Pending' + pending }, { key: 'answered', label: 'Answered' }];
  }

  get filtered(): ResultEvent[] {
    const q = this.search.trim().toLowerCase();
    return q ? this.events.filter((e) => e.name.toLowerCase().includes(q)) : this.events;
  }

  get emptyText(): string {
    if (this.search) {
      return 'Clear the search to see every match.';
    }
    return this.view === 'pending' ? 'No match of this sport and product is waiting for a result.' : 'Try a wider date window.';
  }

  get signedIn(): boolean {
    return AppConfig.requireSignin && authService.isSignedIn();
  }

  get username(): string {
    const s = authService.session();
    return s ? s.username : '';
  }

  get initials(): string {
    return this.username.slice(0, 2).toUpperCase();
  }

  created(): void {
    try {
      const prefs = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
      this.sportId = [1, 2, 3].includes(prefs.sportId) ? prefs.sportId : 1;
      this.product = this.productFor(this.sportId, [1, 2, 3, 4].includes(prefs.product) ? prefs.product : 1);
    } catch (e) {
      // a blocked storage just means the defaults
    }
    this.refresh();
    this.loadCounts();
    this.timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.loadCounts();
      if (this.section === 'results' && !this.loading && !this.dialog.open && !this.bets.open) {
        this.refresh(true);
      }
    }, REFRESH_MS);
  }

  beforeDestroy(): void {
    window.clearInterval(this.timer);
  }

  when(startTime: string | null): string {
    return whenText(startTime);
  }

  private savePrefs(): void {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ sportId: this.sportId, product: this.product }));
    } catch (e) {
      // not kept: fine
    }
  }

  setSection(key: string): void {
    this.section = (['approvals', 'runs', 'debits', 'commission', 'activity'].includes(key) ? key : 'results') as Section;
    this.loadCounts();
  }

  /** GS-903: how much waits in each queue (engine v2 only; an unreachable engine just shows no count). */
  async loadCounts(): Promise<void> {
    const [approvals, blocked, debits] = await Promise.allSettled([loadRuns({ status: 'AWAITING_APPROVAL' }),
      loadRuns({ status: 'BLOCKED' }), loadDebitCases('OPEN')]);
    this.counts = {
      approvals: approvals.status === 'fulfilled' ? approvals.value.length : 0,
      blocked: blocked.status === 'fulfilled' ? blocked.value.length : 0,
      debits: debits.status === 'fulfilled' ? debits.value.length : 0,
    };
  }

  setSport(key: string): void {
    this.sportId = Number(key);
    this.product = this.productFor(this.sportId, this.product);
    this.savePrefs();
    this.close();
    this.refresh();
  }

  setProduct(key: string): void {
    this.product = this.productFor(this.sportId, Number(key));
    this.savePrefs();
    this.close();
    this.refresh();
  }

  setView(key: string): void {
    this.view = key === 'answered' ? 'answered' : 'pending';
    this.close();
    this.refresh();
  }

  /** The list again (quietly: the open match is kept and re-read). */
  async refresh(quiet = false): Promise<void> {
    this.loading = !quiet;
    const { events, errors } = await loadEvents(this.product, this.sportId, this.view, this.from, this.to);
    this.events = events;
    this.errors = errors;
    this.loading = false;
    if (this.selected) {
      // the open match stays open — re-read, even when it left this list (its last market was just answered)
      const again = events.find((e) => e.key === (this.selected as ResultEvent).key);
      if (again) {
        this.selected = again;
      }
      await this.loadSelected(true);
    }
  }

  select(e: ResultEvent): void {
    this.selected = e;
    this.loadSelected();
  }

  close(): void {
    this.selected = null;
    this.markets = [];
    this.marketsError = '';
  }

  private async loadSelected(quiet = false): Promise<void> {
    const event = this.selected;
    if (!event) {
      return;
    }
    this.marketsLoading = !quiet;
    this.marketsError = '';
    try {
      const markets = await loadMarkets(this.product, this.sportId, event, this.view);
      if (this.selected === event) {
        this.markets = markets;
      }
    } catch (e) {
      this.marketsError = (e as Error).message;
    } finally {
      this.marketsLoading = false;
    }
  }

  // ------------------------------------------------------------------------------------------------ declare

  openDeclare(choice: Choice): void {
    const event = this.selected as ResultEvent;
    const m = choice.market;
    const v2 = m.engine === 'v2';
    const fancy = this.product === 3;
    const premium = !!choice.results;
    const was = this.resultWords(m);
    const lines = [event.name + ' · ' + m.name];
    let title = '';
    let eyebrow = 'Declare the result';
    let confirmLabel = 'Confirm';
    let tone = 'primary';
    switch (choice.action) {
      case 'settle':
        title = premium ? `${m.name}: ${choice.resultText}` : fancy ? `${m.name}: ${this.valueText(choice.value, m)}` : `${choice.winnerName} won`;
        confirmLabel = premium || fancy ? 'Save result' : `Declare ${choice.winnerName}`;
        if (premium && this.restText(choice.results as Outcome[])) {
          lines.push(this.restText(choice.results as Outcome[]));
        }
        lines.push(v2 ? 'Every bet on it is settled at once.' : 'Classic result: applied at once.');
        break;
      case 'resettle':
        eyebrow = 'Correct the result';
        title = premium ? `Change to ${choice.resultText}` : fancy ? `Change to ${this.valueText(choice.value, m)}` : `Change to ${choice.winnerName}`;
        confirmLabel = 'Change the result';
        if (premium && this.restText(choice.results as Outcome[])) {
          lines.push(this.restText(choice.results as Outcome[]));
        }
        lines.push('Now: ' + was);
        lines.push(v2 ? 'Every player is settled again — a second supervisor approves it first.' : 'Classic result: replaced at once.');
        break;
      case 'void':
        eyebrow = 'No result';
        title = `No result for ${m.name}`;
        confirmLabel = 'Save: no result';
        if (m.answered && was) {
          // GS-926: voiding a settled market takes the paid money back
          lines.push('Now: ' + was);
          lines.push('Every bet on it is refunded and its settlement taken back — a second supervisor approves it first.');
        } else {
          lines.push('Every bet on it is refunded.');
        }
        break;
      case 'close':
        // GS-926: engine v2's CLOSE — the market takes no more bets; its result comes later
        eyebrow = 'Close the market';
        title = `Close ${m.name}`;
        confirmLabel = 'Close market';
        tone = 'danger';
        lines.push('No more bets are taken on it. Its result is declared later.');
        break;
      case 'unsettle':
        eyebrow = 'Undo the result';
        title = `Undo the result of ${m.name}`;
        confirmLabel = 'Undo the result';
        tone = 'danger';
        lines.push('Now: ' + was);
        lines.push('Every player\'s money goes back to before the result — a second supervisor approves it first.');
        break;
      default:
        break;
    }
    this.choice = choice;
    if (choice.action === 'settle' && m.answered && m.status === 'VOIDED') {
      // GS-926: a market with no result given one after all — the engine asks a second supervisor
      eyebrow = 'Correct the result';
      const atOnce = lines.indexOf('Every bet on it is settled at once.');
      if (atOnce >= 0) {
        lines.splice(atOnce, 1);
      }
      lines.push('Now: No result');
      lines.push('Every bet on it is settled — a second supervisor approves it first.');
    }
    this.dialog = {
      open: true, title, eyebrow, lines, confirmLabel, tone, needsReason: v2,
      voidReasons: v2 && !fancy && choice.action === 'void', reopenOption: v2 && choice.action === 'unsettle',
      forceOption: false, outcomeOption: v2 && (choice.action === 'settle' || choice.action === 'resettle'),
      outcomeDefault: m.lastStop || null, error: '', busy: false,
    };
  }

  /** GS-902: a fancy number as its line reads it — "87 · digit 7", "143 · odd", "76 runs". */
  private valueText(value: number | undefined, m: ResultMarket): string {
    const view = fancyView(value, m.subtype);
    return view ? `${value} · ${view}` : `${value} runs`;
  }

  /** A market's result now, in words: No result, the winner, the number, or (premium) the statement's outcomes. */
  private resultWords(m: ResultMarket): string {
    const r = m.result;
    if (!r) {
      return '';
    }
    if (r.kind === 'VOID') {
      return 'No result';
    }
    if (r.kind === 'OUTCOMES') {
      const names: Record<number, string> = {};
      m.selections.forEach((s) => {
        names[s.id] = s.name;
      });
      return outcomesText(r.outcomes || [], names);
    }
    return r.winnerName || (r.value === null || r.value === undefined ? '' : String(r.value));
  }

  /** GS-894: the title names what did not simply lose; this line says the rest lost, or that nothing won ('' if none lost). */
  private restText(results: Outcome[]): string {
    const lost = results.filter((r) => r.outcome === 'LOSE').length;
    if (lost === results.length) {
      return 'Every selection lost: every bet on this market loses.';
    }
    return lost ? `The other ${lost === 1 ? 'selection' : lost + ' selections'} lost.` : '';
  }

  closeDialog(): void {
    this.dialog.open = false;
    this.choice = null;
  }

  async confirmDeclare(c: Confirmed): Promise<void> {
    const choice = this.choice;
    const event = this.selected;
    if (!choice || !event) {
      return;
    }
    this.dialog.busy = true;
    this.dialog.error = '';
    try {
      const declared = await declare(this.product, this.sportId, event, choice.market, {
        action: choice.action,
        winnerId: choice.winnerId,
        value: choice.value,
        results: choice.results,
        voidReason: choice.action === 'void' ? (this.dialog.voidReasons ? c.voidReason : 'NO_RESULT') : undefined,
        reopen: choice.action === 'unsettle' ? c.reopen : undefined,
        force: c.force || undefined,
        outcomeKnownAt: c.outcomeKnownAt || undefined,
        reason: c.reason || 'Declared on the result console',
        key: idempotencyKey(),
      });
      this.dialog.open = false;
      this.choice = null;
      if (choice.market.engine === 'legacy') {
        this.answeredLocally(choice);
      }
      this.afterDeclare(choice.market, declared, choice.action);
    } catch (e) {
      const err = e as ResultError;
      this.dialog.busy = false;
      if (FORCEABLE.includes(err.code) && (choice.action === 'settle' || choice.action === 'resettle')) {
        this.dialog.forceOption = true;
        this.dialog.error = err.message + ' — tick the box below only if the match is really over.';
      } else {
        this.dialog.error = err.message;
      }
    }
  }

  private afterDeclare(market: ResultMarket, declared: Declared, action?: Declaration['action']): void {
    this.loadCounts();
    if (action === 'close') {
      this.toast('success', `${market.name}: closed — it takes no more bets.`);
    } else if (declared.status === 'AWAITING_APPROVAL') {
      this.toast('info', `${market.name}: sent for approval — a second supervisor must approve it.`);
    } else if (declared.status === 'DONE' || declared.runId === null) {
      this.toast('success', `${market.name}: result saved.`);
    } else {
      this.toast('info', `${market.name}: result accepted — settling…`);
      this.follow(declared.runId, market.name);
    }
    this.refresh(true);
  }

  /** Follows a run until it is DONE (or stops: blocked, waiting for approval), then says so and re-reads. */
  private async follow(runId: number, name: string): Promise<void> {
    for (let i = 0; i < POLL_TRIES; i++) {
      await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
      let status = 'UNKNOWN';
      try {
        status = await runStatus(runId);
      } catch (e) {
        continue;
      }
      if (status === 'DONE') {
        this.toast('success', `${name}: settled.`);
        this.refresh(true);
        return;
      }
      if (status === 'BLOCKED' || status === 'CANCELLED') {
        this.toast('error', `${name}: the settlement is ${status.toLowerCase()} — check the runs queue.`);
        this.refresh(true);
        return;
      }
      if (status === 'AWAITING_APPROVAL') {
        this.refresh(true);
        return;
      }
    }
    this.toast('info', `${name}: still settling — it will show as settled when it is done.`);
  }

  /**
   * A classic result is applied at once and its pending list no longer carries the match: the open panel shows it
   * answered with what was declared (the classic API has nothing to re-read it from).
   */
  private answeredLocally(choice: Choice): void {
    const m = choice.market;
    const result = choice.action === 'void' ? { kind: 'VOID' as const, voidReason: 'NO_RESULT' }
      : choice.value !== undefined ? { kind: 'VALUE' as const, value: choice.value }
        : { kind: 'WINNER' as const, winnerId: choice.winnerId, winnerName: choice.winnerName };
    const answered: ResultMarket = Object.assign({}, m, { answered: true, status: choice.action === 'void' ? 'VOIDED' : 'SETTLED', result });
    this.markets = this.markets.map((x) => (x.marketId === m.marketId ? answered : x));
    if (this.selected && this.selected.legacyMarket && this.selected.legacyMarket.marketId === m.marketId) {
      this.selected = Object.assign({}, this.selected, { legacyMarket: answered });
    }
  }

  // ------------------------------------------------------------------------------------------------ void bets

  async openBets(market: ResultMarket): Promise<void> {
    this.bets = { open: true, market, marketName: market.name, list: [], loading: true, error: '', busy: false };
    try {
      this.bets.list = await loadBets(market.marketId);
    } catch (e) {
      this.bets.error = (e as Error).message;
    } finally {
      this.bets.loading = false;
    }
  }

  async confirmVoidBets(picked: { betIds: number[]; reason: string }): Promise<void> {
    const market = this.bets.market;
    const event = this.selected;
    if (!market || !event) {
      return;
    }
    this.bets.busy = true;
    this.bets.error = '';
    try {
      const declared = await declare(this.product, this.sportId, event, market, {
        action: 'bets/void', betIds: picked.betIds, reason: picked.reason, key: idempotencyKey(),
      });
      this.bets.open = false;
      this.afterDeclare(market, declared);
    } catch (e) {
      this.bets.error = (e as Error).message;
    } finally {
      this.bets.busy = false;
    }
  }

  // ------------------------------------------------------------------------------------------------ the rest

  toast(tone: Toast['tone'], text: string): void {
    const id = ++this.toastSeq;
    this.toasts = [...this.toasts.slice(-3), { id, tone, text }];
    window.setTimeout(() => this.dismiss(id), tone === 'error' ? 9000 : 5000);
  }

  dismiss(id: number): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  signOut(): void {
    authService.signOut();
    this.$router.replace('/');
  }
}
